import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  formatPhoneFilterValue,
  matchTextFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import BranchForm from './BranchForm';
import { fetchBranches, saveBranch, deleteBranch } from '../../../services/gym/catalogApi';
import { usePanelToast } from '../../../context/PanelToastContext';
import { instantLoadState } from '../../../utils/instantLoad';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import {
  exportBranchCsv,
  exportBranchExcel,
  exportBranchPdf,
  exportBranches,
  printBranchList,
} from './exportBranches';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './branches.css';

function StatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span className={`owner-badge ${isActive ? 'owner-badge-success' : 'owner-badge-warning'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function BranchManagement() {
  const toast = usePanelToast();
  const branchBootstrap = instantLoadState('branches');
  const [branchList, setBranchList] = useState(branchBootstrap.initialData);
  const [loading, setLoading] = useState(branchBootstrap.initialLoading);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    name: '',
    location: '',
    manager: '',
    phone: '',
    members: '',
    status: '',
  });

  const loadBranches = useCallback(async () => {
    try {
      setError('');
      if (!branchList.length) setLoading(true);
      setBranchList(await fetchBranches({ startDate: rangeStart, endDate: rangeEnd }));
    } catch (err) {
      setError(err.message || 'Failed to load branches');
    } finally {
      setLoading(false);
    }
  }, [branchList.length, rangeStart, rangeEnd]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  const filteredBranches = useMemo(() => (
    branchList.filter((b) => (
      matchTextFilter(b.name, filters.name)
      && matchTextFilter(b.location, filters.location)
      && matchTextFilter(b.manager, filters.manager)
      && matchTextFilter(b.phone, filters.phone)
      && matchTextFilter(b.members, filters.members)
      && matchTextFilter(b.status === 'active' ? 'Active' : 'Inactive', filters.status)
    ))
  ), [branchList, filters]);

  const handleAddSubmit = async (form) => {
    try {
      const created = await saveBranch(form);
      setBranchList((prev) => [...prev, created]);
      setShowAddModal(false);
      toast.showAdded('Branch');
    } catch (err) {
      setError(err.message || 'Failed to add branch');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteBranch(deleteTarget.id);
      setBranchList((prev) => prev.filter((b) => b.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.showDeleted('Branch');
    } catch (err) {
      setError(err.message || 'Failed to delete branch');
    }
  };

  return (
    <div>
      <PageHeader
        title="Branch Management"
        subtitle="Manage gym branches, locations, and performance"
      >
        <button type="button" className="owner-btn owner-btn-primary" onClick={() => { setFormKey((k) => k + 1); setShowAddModal(true); }}>
          Add Branch
        </button>
      </PageHeader>

      <PrintActionsHeader
        startDate={rangeStart}
        endDate={rangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={() => { setRangeStart(''); setRangeEnd(''); }}
        onPrint={() => printBranchList(filteredBranches)}
        onCsv={() => exportBranchCsv(filteredBranches)}
        onPdf={() => exportBranchPdf(filteredBranches)}
        onExcel={() => exportBranchExcel(filteredBranches)}
        onExport={() => exportBranches(filteredBranches)}
        ariaLabel="Branch print and export"
      />

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading branches...</p>}

      <div className="owner-card rec-panel-card rec-member-list-card admin-list-table-card owner-mb-28">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Branch Name"
                  filterKey="name"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Location"
                  filterKey="location"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search location..."
                />
                <ColumnFilterHeader
                  label="Manager"
                  filterKey="manager"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search manager..."
                />
                <ColumnFilterHeader
                  label="Phone"
                  filterKey="phone"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="e.g. 0300-1234567"
                  formatValue={formatPhoneFilterValue}
                />
                <ColumnFilterHeader
                  label={rangeStart || rangeEnd ? 'Members (range)' : 'Members'}
                  filterKey="members"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search members..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBranches.length > 0 ? (
                filteredBranches.map((branch) => (
                  <tr key={branch.id}>
                    <td>{branch.name}</td>
                    <td>{branch.location}</td>
                    <td>{branch.manager}</td>
                    <td>{branch.phone}</td>
                    <td>{branch.members}</td>
                    <td><StatusBadge status={branch.status} /></td>
                    <td>
                      <div className="rec-member-actions-cell">
                        <Link to={`/admin/branches/edit/${branch.id}`} className="owner-btn owner-btn-secondary owner-btn-sm">Edit</Link>
                        <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => setDeleteTarget(branch)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="owner-placeholder-text">No branches yet. Add a branch before creating packages.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="subscription-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Add Branch</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setShowAddModal(false)} aria-label="Close">×</button>
            </div>
            <BranchForm key={formKey} submitLabel="Add Branch" onSubmit={handleAddSubmit} onCancel={() => setShowAddModal(false)} />
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Branch</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <div className="subscription-form">
              <p className="membership-modal-message">Delete branch <strong>{deleteTarget.name}</strong>?</p>
              <div className="subscription-form-actions">
                <button type="button" className="owner-btn membership-btn-danger" onClick={handleDelete}>Delete</button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BranchManagement;
